import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { usePackingSlipFormat } from '../../../../../../../../../hooks/use-seller-setting';
import { useVisiblePackingSlips } from '../../../../../../../../../hooks/use-visible-packing-slips';
import Global from '../../../../../../../../../store/global-selectors';
import {
  ACTION_TYPE_USE_PACKING_SLIP,
  ACTION_TYPE_USE_DELIVERY_NOTIFICATION_EMAIL_TEMPLATE,
  ACTION_TYPE_USE_SHIPMENT_NOTIFICATION_TEMPLATE,
  ACTION_TYPE_DELEGATE_TO_ASSIGNED_FULFILLMENT_PROVIDER,
} from '../../../../../../constants/action-types';
import InputValueProps from '../../../../../../prop-types/input-value-props';
import { propertyForUpdateByActionType } from '../../../helpers/property-for-update-by-action-type';
import SingleSelectDropdown from './single-select-dropdown';
import { PackingSlipFormat } from '../../../../../../../../../pages/settings/printing/document-option-modal/packing-slip/constants';

const DBBasedCollectionDropdown = ({ actionTypeId, inputValueProps, onChange, label }) => {
  const packingSlipFormat = usePackingSlipFormat();
  const propertyForUpdate = propertyForUpdateByActionType(actionTypeId);
  const packingSlips = useVisiblePackingSlips();
  const deliveryTemplates = useSelector(Global.selectSellerDeliveryTemplates);
  const shipmentTemplates = useSelector(Global.selectSellerShipmentTemplates);
  const fulfillmentProviderServices = useSelector(Global.selectSellerFulfillmentServices);

  const memoizedPackingSlips = useMemo(() => {
    const templateFilter = packingSlipFormat === PackingSlipFormat.HalfLetter ? PackingSlipFormat.Letter : packingSlipFormat;
    return packingSlips.reduce((arr, packingSlip) => {
      if (templateFilter === packingSlip.size) {
        arr.push({
          ...packingSlip,
          [propertyForUpdate]: Number.parseInt(packingSlip.packingSlipId, 10),
        });
      }
      return arr;
    }, []);
  }, [packingSlipFormat, packingSlips, propertyForUpdate]);
  const memoizedDeliveryTemplates = useMemo(() => {
    return deliveryTemplates.map((dt) => ({
      ...dt,
      [propertyForUpdate]: Number.parseInt(dt.emailTemplateId, 10),
    }));
  }, [deliveryTemplates, propertyForUpdate]);
  const memoizedShipmentTemplates = useMemo(() => {
    return shipmentTemplates.map((st) => ({
      ...st,
      [propertyForUpdate]: Number.parseInt(st.emailTemplateId, 10),
    }));
  }, [shipmentTemplates, propertyForUpdate]);
  const memoizedFulfillmentProviderServices = useMemo(() => {
    return fulfillmentProviderServices.map((service) => ({
      [propertyForUpdate]: Number.parseInt(service.fulfillmentServiceId, 10),
      name: service?.name || '',
    }));
  }, [fulfillmentProviderServices, propertyForUpdate]);

  const typeToAllowedActionsList = {
    [ACTION_TYPE_USE_PACKING_SLIP]: memoizedPackingSlips,
    [ACTION_TYPE_USE_DELIVERY_NOTIFICATION_EMAIL_TEMPLATE]: memoizedDeliveryTemplates,
    [ACTION_TYPE_USE_SHIPMENT_NOTIFICATION_TEMPLATE]: memoizedShipmentTemplates,
    [ACTION_TYPE_DELEGATE_TO_ASSIGNED_FULFILLMENT_PROVIDER]: memoizedFulfillmentProviderServices,
  };
  return (
    <SingleSelectDropdown
      actionTypeId={actionTypeId}
      allowedActions={typeToAllowedActionsList[actionTypeId]}
      inputValueProps={inputValueProps}
      label={label}
      onChange={onChange}
    />
  );
};

DBBasedCollectionDropdown.propTypes = {
  actionTypeId: PropTypes.number.isRequired,
  inputValueProps: InputValueProps.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.node.isRequired,
};

export default DBBasedCollectionDropdown;
